import { i18n } from 'next-i18next'

export interface StripeErrorParts {
  title: string
  message: string
}

export function getStripeErrorParts(declineCode: string | undefined): StripeErrorParts {
  const t = i18n?.t.bind(i18n) || ((_key: string, fallback: string) => fallback)

  // Helper function to get title and message
  const getErrorParts = (
    titleKey: string,
    messageKey: string,
    fallbackTitle: string,
    fallbackMessage: string,
  ) => {
    const title = t(titleKey, fallbackTitle)
    const message = t(messageKey, fallbackMessage)

    return {
      title,
      message,
    }
  }

  // Default error
  const defaultError = {
    title: '',
    message: t(
      'Stripe.errors.default',
      'An error has occurred while trying to finalize your payment.',
    ),
  }

  switch (declineCode) {
    case 'generic_decline':
      return getErrorParts(
        'Stripe.errors.genericDecline.title',
        'Stripe.errors.genericDecline.message',
        'Card declined',
        'Contact your card issuer or try another payment method.',
      )
    case 'insufficient_funds':
      return getErrorParts(
        'Stripe.errors.insufficientFunds.title',
        'Stripe.errors.insufficientFunds.message',
        'Insufficient funds',
        'Try again with another payment method.',
      )
    case 'expired_card':
      return getErrorParts(
        'Stripe.errors.cardExpired.title',
        'Stripe.errors.cardExpired.message',
        "Your card's expired",
        'Try again with another payment method.',
      )
    case 'incorrect_cvc':
      return getErrorParts(
        'Stripe.errors.incorrectCVC.title',
        'Stripe.errors.incorrectCVC.message',
        'Incorrect CVC',
        'Check your CVC number and try again.',
      )
    case 'processing_error':
      return getErrorParts(
        'Stripe.errors.processingError.title',
        'Stripe.errors.processingError.message',
        'Something went wrong',
        "Your payment didn't go through. Try again.",
      )
    case 'card_velocity_exceeded':
      return getErrorParts(
        'Stripe.errors.cardVelocityExceeded.title',
        'Stripe.errors.cardVelocityExceeded.message',
        'Insufficient funds',
        'Try again with another payment method.',
      )
    // Authentication errors
    case 'authentication_required':
    case 'payment_intent_authentication_failure':
      return getErrorParts(
        'Stripe.errors.authenticationRequired.title',
        'Stripe.errors.authenticationRequired.message',
        'Card declined',
        'Try again with another payment method.',
      )
    // Security-related errors (should be presented as generic decline for security)
    case 'lost_card':
    case 'stolen_card':
    case 'pickup_card':
    case 'restricted_card':
    case 'fraudulent':
    case 'merchant_blacklist':
      // Don't reveal specific security reasons to customer
      return getErrorParts(
        'Stripe.errors.genericDecline.title',
        'Stripe.errors.genericDecline.message',
        'Card declined',
        'Contact your card issuer or try another payment method.',
      )

    // Card validation errors
    case 'incorrect_number':
    case 'invalid_number':
      return getErrorParts(
        'Stripe.errors.incorrectNumber.title',
        'Stripe.errors.incorrectNumber.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'incorrect_zip':
      return getErrorParts(
        'Stripe.errors.incorrectZip.title',
        'Stripe.errors.incorrectZip.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'incorrect_address':
      return getErrorParts(
        'Stripe.errors.incorrectAddress.title',
        'Stripe.errors.incorrectAddress.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'invalid_expiry_month':
    case 'invalid_expiry_year':
      return getErrorParts(
        'Stripe.errors.invalidExpiry.title',
        'Stripe.errors.invalidExpiry.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'invalid_cvc':
      return getErrorParts(
        'Stripe.errors.incorrectCVC.title',
        'Stripe.errors.incorrectCVC.message',
        'Incorrect CVC',
        'Check your CVC number and try again.',
      )

    // Card not supported errors
    case 'card_not_supported':
      return getErrorParts(
        'Stripe.errors.cardNotSupported.title',
        'Stripe.errors.cardNotSupported.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'currency_not_supported':
      return getErrorParts(
        'Stripe.errors.currencyNotSupported.title',
        'Stripe.errors.currencyNotSupported.message',
        'Card declined',
        'Try again with another payment method.',
      )

    // Account/limit errors
    case 'invalid_account':
      return getErrorParts(
        'Stripe.errors.invalidAccount.title',
        'Stripe.errors.invalidAccount.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'withdrawal_count_limit_exceeded':
      return getErrorParts(
        'Stripe.errors.withdrawalLimitExceeded.title',
        'Stripe.errors.withdrawalLimitExceeded.message',
        'Insufficient funds',
        'Try again with another payment method.',
      )
    case 'invalid_amount':
      return getErrorParts(
        'Stripe.errors.invalidAmount.title',
        'Stripe.errors.invalidAmount.message',
        'Card declined',
        'Try again with another payment method.',
      )

    // Issuer communication errors
    case 'call_issuer':
    case 'approve_with_id':
    case 'issuer_not_available':
      return getErrorParts(
        'Stripe.errors.contactIssuer.title',
        'Stripe.errors.contactIssuer.message',
        'Card declined',
        'Contact your card issuer or try another payment method.',
      )

    // Transaction errors
    case 'duplicate_transaction':
      return getErrorParts(
        'Stripe.errors.duplicateTransaction.title',
        'Stripe.errors.duplicateTransaction.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'do_not_honor':
    case 'not_permitted':
    case 'transaction_not_allowed':
    case 'service_not_allowed':
      return getErrorParts(
        'Stripe.errors.transactionNotAllowed.title',
        'Stripe.errors.transactionNotAllowed.message',
        'Card declined',
        'Try again with another payment method.',
      )

    // PIN-related errors
    case 'incorrect_pin':
    case 'offline_pin_required':
    case 'online_or_offline_pin_required':
      return getErrorParts(
        'Stripe.errors.pinRequired.title',
        'Stripe.errors.pinRequired.message',
        'Card declined',
        'Try again with another payment method.',
      )
    case 'pin_try_exceeded':
      return getErrorParts(
        'Stripe.errors.pinTriesExceeded.title',
        'Stripe.errors.pinTriesExceeded.message',
        'Card declined',
        'Try again with another payment method.',
      )

    // Retry-related errors
    case 'reenter_transaction':
      return getErrorParts(
        'Stripe.errors.reenterTransaction.title',
        'Stripe.errors.reenterTransaction.message',
        'Something went wrong',
        "Your payment didn't go through. Try again.",
      )

    // Test mode
    case 'testmode_decline':
      return getErrorParts(
        'Stripe.errors.testMode.title',
        'Stripe.errors.testMode.message',
        'Card declined',
        'Try again with another payment method.',
      )

    // Generic unknown errors
    case 'no_action_taken':
    case 'revocation_of_authorization':
    case 'revocation_of_all_authorizations':
    case 'security_violation':
    case 'stop_payment_order':
    case 'new_account_information_available':
    default:
      return defaultError
  }
}
